import { createAppButton } from 'owa-command-ribbon';
import { getReadRibbonId } from 'owa-mail-ribbon-utils/lib/getReadRibbonId';
import { getComposeRibbonId } from 'owa-compose-ribbon-utils/lib/utils/getComposeRibbonId';
import type { IButtonStyles } from '@fluentui/react';
import type { AppButtonOnExecuteParameter, RibbonControlProps } from 'owa-acui';
import type { AddinCommandSurfaceItem } from 'owa-addins-types';
import type { ReadOnlyRibbonControlDefProps } from 'owa-mail-ribbon-utils';
import { getComputedCallback } from 'owa-ribbon-mobx-utils/lib/getComputedCallback';
import { getIsReadButtonDisabled } from 'owa-mail-ribbon-utils/lib/getIsReadButtonDisabled';
import { getRibbonMailboxInfo } from 'owa-mail-ribbon-utils/lib/getRibbonMailboxInfo';
import { owaComputedFn } from 'owa-computed-fn';
import { getAddinIcon } from 'owa-addins-surface-actions/lib/utils/getAddinIcon';
import {
    isNoItemContext,
    isMultipleSelection,
} from 'owa-message-extension-flyout/lib/utils/messageExtensionItemContext';
import type { RuntimeControlRibbonCapabilites } from 'owa-m365-acquisitions/lib/utils/runtimeControlRibbonCapabilites';
import { ribbonPinnedMessageExtensionOnExecute } from 'owa-message-extension-flyout/lib/utils/ribbonPinnedMessageExtensionOnExecute';
import { isOffline } from 'owa-offline';

export function createPinnedAddInButton(
    addIn: AddinCommandSurfaceItem,
    composeId: string,
    editorId: string,
    controlId: number,
    targetWindow: Window,
    styles: IButtonStyles
): RibbonControlProps {
    const composeRibbonId = getComposeRibbonId(controlId, editorId);
    return createAppButton(
        composeRibbonId /* controlId */,
        addIn?.title ?? addIn?.name ?? '' /* label */,
        (params?: AppButtonOnExecuteParameter) => {
            if (params) {
                getComputedCallback(
                    controlId,
                    ribbonPinnedMessageExtensionOnExecute,
                    addIn.key,
                    targetWindow,
                    'MessageCompose' /* entrypoint */,
                    '' /* itemId */,
                    composeId,
                    params
                )();
            }
        },
        undefined /* keyTip */,
        getAddinIcon(addIn) ?? '' /* iconName */,
        undefined /* iconColor */,
        styles,
        {
            customTooltip: addIn.ariaLabel?.replace(/\s-\s/g, '\n\n'),
            ariaDescription: addIn.ariaLabel,
            disabled: isOffline(),
        } /* overrides */
    );
}

/**
 * Create a button for an add-in in the read ribbon.
 *
 * @export
 * @param {AddinCommandSurfaceItem} addIn
 * @param {number} controlId
 * @param {Window} targetWindow
 * @param {IButtonStyles} styles
 * @param {ReadOnlyRibbonControlDefProps} props
 * @returns {RibbonControlProps}
 */
export function createAddinButtonForReadInRibbon(
    addIn: AddinCommandSurfaceItem,
    controlId: number,
    targetWindow: Window,
    styles: IButtonStyles,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const { isPopout, itemId } = props;
    const readRibbonId = getReadRibbonId(controlId, itemId);
    const params = {} as AppButtonOnExecuteParameter;
    params.id = readRibbonId as string;

    return createAppButton(
        readRibbonId /* controlId */,
        addIn?.title ?? addIn?.name ?? '' /* label */,
        getComputedCallback(
            controlId,
            ribbonPinnedMessageExtensionOnExecute,
            addIn.key,
            targetWindow,
            'MessageRead' /* entrypoint */,
            itemId?.Id,
            '' /* composeId */,
            params
        ) /* onExecute handler */,
        undefined /* keyTip */,
        getAddinIcon(addIn) ?? '' /* iconName */,
        undefined /* iconColor */,
        styles,
        {
            disabled: getIsReadButtonDisabled(
                controlId,
                isPopout,
                getRibbonMailboxInfo(props.mailboxInfo),
                itemId
            ),
            customTooltip: addIn.ariaLabel,
            ariaDescription: addIn.ariaLabel,
        } /* overrides */
    );
}

export const getReadRibbonRuntimeControlProps: (
    addIn: AddinCommandSurfaceItem,
    controlId: number,
    targetWindow: Window,
    styles: IButtonStyles,
    props: ReadOnlyRibbonControlDefProps,
    ribbonContext: RuntimeControlRibbonCapabilites
) => RibbonControlProps = owaComputedFn(function getReadRibbonRuntimeControlProps(
    addIn: AddinCommandSurfaceItem,
    controlId: number,
    targetWindow: Window,
    styles: IButtonStyles,
    props: ReadOnlyRibbonControlDefProps,
    ribbonContext: RuntimeControlRibbonCapabilites
): RibbonControlProps {
    const { isPopout, itemId } = props;
    const readRibbonId = getReadRibbonId(controlId, itemId);
    const ribbonMailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);

    const isMultiSelect = isMultipleSelection(props.isPopout);
    const isNoContext = isNoItemContext();
    const messageItemId = isMultiSelect || isNoContext ? '' : itemId?.Id ?? undefined;

    const isAddinDisabledForMultipleSelection = isNoContext && !ribbonContext.isEnabledInNoContext;
    const isAddinDisabledForNoItemContext = isMultiSelect && !ribbonContext.isEnabledInMultiSelect;

    const entryPoint = isNoContext
        ? 'NoItemContext'
        : isMultiSelect
        ? 'MultipleSelection'
        : 'MessageRead';

    return createAppButton(
        readRibbonId /* controlId */,
        addIn?.title ?? addIn?.name ?? '' /* label */,
        (params?: AppButtonOnExecuteParameter) => {
            if (params) {
                getComputedCallback(
                    controlId,
                    ribbonPinnedMessageExtensionOnExecute,
                    addIn.key,
                    targetWindow,
                    entryPoint,
                    messageItemId,
                    '' /* composeId */,
                    params
                )();
            }
        } /* onExecute handler */,
        undefined /* keyTip */,
        getAddinIcon(addIn) ?? '' /* iconName */,
        undefined /* iconColor */,
        styles,
        {
            disabled:
                isOffline() ||
                getIsReadButtonDisabled(controlId, isPopout, ribbonMailboxInfo, itemId) ||
                isAddinDisabledForMultipleSelection ||
                isAddinDisabledForNoItemContext,
            customTooltip: addIn.ariaLabel,
            ariaDescription: addIn.ariaLabel,
        } /* overrides */
    );
});
